import { useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { X, Key, CheckCircle, Loader2, LogIn } from 'lucide-react';
import { setTier } from '../lib/license';
import { useAuth } from '../hooks/useAuth';
import { getIdToken } from '../lib/auth';
import { ensureLicenseToken } from '../lib/licenseToken';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

export default function ActivationModal({ open, onClose, onActivated }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  async function handleActivate(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const normalizedCode = code.toUpperCase().trim();
      const token = await getIdToken();
      const response = await fetch(`${API_URL}/account/link-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: normalizedCode }),
      });
      const data = await response.json();
      if (!data.success) {
        // Server returns the appropriate copy per case (404 / 409 / 410).
        // Fall through to a generic message only if the server didn't say.
        setError(data.message || 'Activation failed. Please check your code and try again.');
        return;
      }
      setTier(data.tier);
      // Code just activated on this device — issue the offline license token.
      await ensureLicenseToken().catch(() => {});
      setSuccess(true);
      if (onActivated) onActivated(data.tier);

      setTimeout(() => {
        onClose();
        setSuccess(false);
        setCode('');
      }, 1500);
    } catch {
      setError('Could not connect to the activation server. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleCodeChange(e) {
    let val = e.target.value.toUpperCase().replace(/[^23456789ABCDEFGHJKMNPQRSTUVWXYZ-]/g, '');
    const raw = val.replace(/-/g, '').replace(/^SCRV/, '');
    if (raw.length <= 12) {
      const parts = ['SCRV'];
      for (let i = 0; i < raw.length; i += 4) {
        parts.push(raw.slice(i, i + 4));
      }
      val = parts.join('-');
    }
    setCode(val);
  }

  // Codes bind to accounts, not devices. Web AND desktop require sign-in.
  const showSignInPrompt = !user;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-brand-blue" />
            <h2 className="text-lg font-semibold">Enter Registration Code</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-green-700">Activated!</p>
              <p className="text-sm text-gray-500 mt-1">Your license has been activated successfully.</p>
            </div>
          ) : showSignInPrompt ? (
            <div className="text-center py-2">
              <p className="text-sm text-gray-600 mb-6">
                Create an account or sign in to activate your code. Codes link to your account so they
                work on any supported device.
              </p>
              <button
                type="button"
                onClick={() => { onClose(); navigate('/activate'); }}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark"
              >
                <LogIn className="w-4 h-4" />
                Create account or sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleActivate}>
              <p className="text-sm text-gray-600 mb-4">
                Enter the registration code from your Scrivar card or email.
              </p>

              <input
                type="text"
                value={code}
                onChange={handleCodeChange}
                placeholder="SCRV-XXXX-XXXX-XXXX"
                className="w-full px-4 py-3 border rounded-lg text-center font-mono text-lg tracking-wider focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                maxLength={19}
                autoFocus
                disabled={loading}
              />

              {error && (
                <p className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || code.length < 19}
                className="w-full mt-4 px-4 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Activating...
                  </>
                ) : (
                  'Activate'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
